/**
 * Direct-URL check for custom staff (Member / custom access).
 *
 * The nav already hides what a staff member was not given. This stops the
 * same pages from opening by URL, bookmark, or a button outside the nav
 * (the Partners button in the top bar). A path that no nav row points to
 * (an order, a batch, a print station) stays open.
 */

import { MAIN_NAV } from "./mainNav";
import { navItemPath, SETTINGS_NAV } from "./settingsNav";
import { hasStaffPermission, isStaffAccessRestricted } from "./staffAccess";
import { isEnteredStationPath } from "./stationChoice";

/** Pages every signed-in staff member keeps: their own profile and help. */
const ALWAYS = new Set(["profile", "settings", "help"]);

/** Grant for the Partners list (`/users`). It has no nav row of its own. */
export const PARTNERS_PERMISSION = "partners";

const EXTRA = [{ path: "/users", ids: [PARTNERS_PERMISSION] }];

const cleanPath = (value) => {
  const path = String(value || "").split(/[?#]/)[0].replace(/\/+$/, "");
  return path || "/";
};

const collect = () => {
  const byPath = new Map();
  const add = (to, id) => {
    if (!to || !id || /^https?:/i.test(String(to))) return;
    const path = cleanPath(to);
    if (path === "/" || path === "/#") return;
    if (!byPath.has(path)) byPath.set(path, new Set());
    byPath.get(path).add(id);
  };
  const walkMain = (items) =>
    (items || []).forEach((item) => {
      if (!item) return;
      if (item.type === "parent") walkMain(item.children);
      else add(item.to, item.id);
    });
  walkMain(MAIN_NAV);
  SETTINGS_NAV.forEach((group) =>
    (group.items || []).forEach((item) => {
      if (item.type === "parent") {
        (item.children || []).forEach((child) => add(navItemPath(child), child.id));
      } else {
        add(navItemPath(item), item.id);
      }
    })
  );
  EXTRA.forEach((rule) => rule.ids.forEach((id) => add(rule.path, id)));
  // Longest path first so /settings/billing/payments wins over /settings/billing.
  return [...byPath.entries()]
    .map(([path, ids]) => ({ path, ids: [...ids] }))
    .sort((a, b) => b.path.length - a.path.length);
};

let rules = null;
const pathRules = () => {
  if (!rules) rules = collect();
  return rules;
};

/** The nav permission ids that guard this path, or null when no row points here. */
export function staffPathPermissions(pathname) {
  const path = cleanPath(pathname);
  const rule = pathRules().find(
    (entry) => path === entry.path || path.startsWith(`${entry.path}/`)
  );
  return rule ? rule.ids : null;
}

export function staffPathAllowed(user, pathname) {
  if (!isStaffAccessRestricted(user)) return true;
  // Station screens (DTG printing, QC, shipping, …) are where floor staff work.
  if (isEnteredStationPath(pathname)) return true;
  const ids = staffPathPermissions(pathname);
  if (!ids) return true;
  if (ids.some((id) => ALWAYS.has(id))) return true;
  return ids.some((id) => hasStaffPermission(user, id));
}
